import { CheckCircleIcon } from '@braskai/icons';
import { Button, Icon, Link, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useLocation, useNavigate } from 'react-router-dom';

import type { TrialPlan } from '@/features/pricing/trialPaywall/model/PLANS';

import { Stack, Typography } from '@mui/material';

import { useTrialPaywallModalStore } from '@/features/pricing/trialPaywall/model/useTrialPaywallModalStore';

import { useBalanceStore } from '@/entities/balance';

import { RASK_CHAT_LINK } from '@/shared/constants/docLinks';
import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { createTextParser } from '@/shared/lib/createTextParser';
import { ModalHeaderSecondary } from '@/shared/ui/modal';

import { DATA_TEST_IDS } from '../../lib/constants';
import { BUTTON_TEXTS } from '../../lib/limitedFeatures/texts';
import { LimitedFeature } from '../../lib/types';
import { useUpgradePlanModalContent } from '../../model/useUpgradePlanModalContent';
import { useUpgradePlanModalStore } from '../../model/useUpgradePlanModalStore';
import { SDescriptionText, SImage, SNativeVideoPlayer } from './styled';

const UpgradePlanModalComponent = ({ isSpaceOwner }: { isSpaceOwner: boolean }) => {
  const limitedFeature = useUpgradePlanModalStore((state) => state.limitedFeature);
  const plan = useUpgradePlanModalStore((state) => state.plan);
  const close = useUpgradePlanModalStore((state) => state.close);

  const hasTrialPlan = useBalanceStore((state) => state.hasTrialPlan);

  const navigate = useNavigate();
  const location = useLocation();

  const { content } = useUpgradePlanModalContent(limitedFeature as LimitedFeature, plan);
  // Хук открытия TrialPaywall с кастомными данными
  const openTrialPaywall = useTrialPaywallModalStore((state) => state.open);

  const { checkIsSpaceOwner = true } = content ?? {};

  useExecuteOneTime(() => {
    content?.mixpanelOpeningEvent();
  });

  if (!content) {
    console.error('Should show upgrade modal but no content for this plan category was provided');

    return null;
  }

  const {
    title,
    bulletsHeader,
    buttonText,
    caption,
    features,
    media: { poster, src, type: mediaType, videoOptions },
    mixpanelButtonClickEvent,
    subtitle,
  } = content;

  const handleButtonClick = () => {
    mixpanelButtonClickEvent();

    if (buttonText === BUTTON_TEXTS.contactUs) {
      window.open(RASK_CHAT_LINK, '_blank');
      return;
    } else {
      // Если контент содержит рекомендуемый план — открываем TrialPaywall с ним
      const selectedPlan = content?.content?.selectedPlan;
      if (selectedPlan) {
        openTrialPaywall({
          preSelectedPlanName: selectedPlan as TrialPlan['name'],
        });
      } else {
        navigate(`/${PATHS.pricing}`, { state: { path: location.pathname } });
      }
    }

    close();
  };

  const parseLinksAndBoldAndGrayText = createTextParser(Link, SDescriptionText);

  return (
    <Modal size="xs" open onClose={close}>
      <ModalHeaderSecondary title={title} subtitle={subtitle} onClose={close} />

      <ModalContent noPadding>
        {mediaType === 'video' ? (
          <SNativeVideoPlayer
            sx={{
              margin: '0 auto',
              maxWidth: hasTrialPlan ? '356px' : '100%',
            }}
            poster={poster}
            src={src}
            {...videoOptions}
          />
        ) : (
          <SImage $isGif={mediaType === 'gif'} src={src} />
        )}

        {(features?.length ?? 0) > 0 && (
          <>
            <Typography color="grey.700" marginTop={8} variant="caption-1-md">
              {parseLinksAndBoldAndGrayText(caption)}
            </Typography>

            <Typography color="grey.700" component="h3" marginTop={16} variant="body-1-md">
              {bulletsHeader}
            </Typography>

            <Stack gap={12} marginBottom={4} marginTop={12}>
              {features.map(({ description, icon }) => (
                <Stack key={description} direction="row" gap={8}>
                  <Icon color="primary.600" component={icon} size={20} />
                  <Typography color="grey.700" variant="body-2-md">
                    {parseLinksAndBoldAndGrayText(description)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {hasTrialPlan && content.content && (
          <Stack marginTop={20}>
            <Typography color="grey.900" variant="body-2-md">
              {content.content.whatYouCanDo.title}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {content.content.whatYouCanDo.bullets.map((bullet) => (
                <li key={bullet}>
                  <Typography color="grey.700" variant="body-2">
                    {bullet}
                  </Typography>
                </li>
              ))}
            </ul>
            <Typography color="grey.700" marginTop={12} variant="body-2">
              <span style={{ fontWeight: 500 }}>{content.content.perfectFor.title}</span>{' '}
              {content.content.perfectFor.text}
            </Typography>
            <Typography color="grey.700" marginTop={12} variant="body-2">
              <Link href={content.content.learnMore.link} rel="noreferrer noopener" size="md" target="_blank">
                {content.content.learnMore.title}
              </Link>{' '}
              <span style={{ fontWeight: 500 }}>{content.content.learnMore.text}</span>{' '}
            </Typography>
            <Stack
              sx={{
                backgroundColor: 'grey.100',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 16,
                marginTop: '12px',
              }}
              direction="row"
              padding={15}
            >
              <Stack flexDirection="column" gap={2}>
                <Stack flexDirection="row" gap={10}>
                  <CheckCircleIcon height={20} width={20} />
                  <Typography color="grey.900" variant="body-2-md">
                    {content.content.chip.title}
                  </Typography>
                </Stack>
                {content.content.chip.text && (
                  <Typography color="grey.700" marginLeft={30} variant="body-2">
                    {content.content.chip.text}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </ModalContent>

      <ModalActions>
        {!IS_WHITE_LABEL && (
          <Button
            data-testid={DATA_TEST_IDS.upgradeButton}
            disabled={checkIsSpaceOwner && !isSpaceOwner}
            size="lg"
            variant="primary"
            fullWidth
            onClick={handleButtonClick}
          >
            {buttonText}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export const UpgradePlanModal = ({ isSpaceOwner }: { isSpaceOwner: boolean }) => (
  <ModalWrapper hook={useUpgradePlanModalStore}>
    <UpgradePlanModalComponent isSpaceOwner={isSpaceOwner} />
  </ModalWrapper>
);
